<i18n
  src="@/locales/views/agro_exporter/providers/producers/show_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_producers')"
    :params="{
      agro_exporter_id: agro_exporter_id
    }"
  />

  <bread-crumbs
    :labels="[module_name, producer?.agro_exporter_producer?.producer_code]"
  />

  <alert-danger :errors_data="std_err" />

  <template v-if="load_general_data">
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.organization_producer.producer_code") }}
                </dt>
                <dd class="content-data">
                  {{ producer.agro_exporter_producer.producer_code }}
                  <span class="space-icon" />
                  <yes-no-label
                    :value="producer.agro_exporter_producer.active"
                    yes="Activo"
                    no="Inactivo"
                  />
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.producer_code") }}
                </dt>
                <dd class="content-data">
                  {{ producer.producer_code }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.lastname") }}
                </dt>
                <dd class="content-data">
                  {{ producer.lastname }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.name") }}
                </dt>
                <dd class="content-data">
                  {{ producer.name }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.gender_id") }}
                </dt>
                <dd class="content-data">
                  {{ genderName(producer.gender_id) }}
                </dd>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <destroy-button
              v-if="!proc_button_destroy"
              :disabled_button="false"
              @destroyEvent="destroyProducer()"
              :label="$t('labels.destroy_button_label')"
              :title="$t('labels.destroy_button_title')"
              :message="$t('labels.destroy_button_message')"
              :label_confirm="$t('labels.destroy_button_label_confirm')"
            />
            <destroy-button-processing
              v-else
              :label="$t('labels.destroy_button_label')"
            />
            <span class="space-actions"></span>
            <edit-button
              @click.prevent="goToEditProducer()"
              :label="$t('labels.edit_button')"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="my-8">
      <div class="flex justify-between mt-8 mb-2">
        <h3 class="items-center">
          <div class="panel-header-title">{{ $t("labels.crop_fields") }}</div>
        </h3>

        <new-button
          @click.prevent="goToNewCropField"
          :label="$t('labels.new_crop_field_button')"
        />
      </div>

      <div class="mt-2 flex flex-col">
        <div class="table-first-container">
          <div class="table-second-container">
            <div class="table-third-container">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.name") }}
                    </th>
                    <th scope="col" class="table-header-cell">Área</th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.location") }}
                    </th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.crop_id") }}
                    </th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.planting_date") }}
                    </th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.harvest_time") }}
                    </th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.tree_number") }}
                    </th>
                    <th scope="col" class="table-header-cell">
                      Coordenadas (DD)
                    </th>
                    <th scope="col" class="table-header-cell">
                      {{ $t("model.crop_field.altitude") }}
                    </th>
                    <th scope="col" class="table-header-cell"></th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr
                    class="table-row"
                    v-for="crop_field in crop_fields"
                    :key="crop_field.id"
                  >
                    <td class="table-cell">{{ crop_field.name }}</td>
                    <td class="table-cell">
                      {{ numberWithPrecision(crop_field.hectares) }} ha
                      <br />
                      {{ numberWithPrecision(crop_field.square_meters) }} m²
                    </td>
                    <td class="table-cell">{{ crop_field.location }}</td>
                    <td class="table-cell">
                      {{ crop_field.crop.name }}
                      <br />
                      ({{ crop_field.crop_variety.name }})
                    </td>
                    <td class="table-cell">
                      {{ dateFormatter(crop_field.planting_date, "MMM YYYY") }}
                    </td>
                    <td class="table-cell">{{ crop_field.harvest_time }}</td>
                    <td class="table-cell">
                      {{ numberWithPrecision(crop_field.tree_number) }}
                    </td>
                    <td class="table-cell">
                      {{ crop_field.longitude_dd }},
                      {{ crop_field.latitude_dd }}
                    </td>
                    <td class="table-cell">
                      {{ numberWithPrecision(crop_field.altitude) }}
                    </td>
                    <td class="table-cell">
                      <destroy-button
                        v-if="!proc_button_destroy"
                        :disabled_button="false"
                        @destroyEvent="destroyCropField(crop_field)"
                        :label="$t('labels.destroy_crop_field_button_label')"
                        :title="`${$t(
                          'labels.destroy_crop_field_button_title'
                        )}: ${crop_field.name}`"
                        :message="
                          $t('labels.destroy_crop_field_button_message')
                        "
                        :label_confirm="
                          $t('labels.destroy_crop_field_button_label_confirm')
                        "
                      />
                      <destroy-button-processing
                        v-else
                        :label="$t('labels.destroy_crop_field_button_label')"
                      />
                      <span class="space-actions"></span>
                      <edit-button
                        @click.prevent="goToEditCropField(crop_field)"
                        :label="$t('labels.edit_button')"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/providers/producers/manage_producers.js";
import RequesterCropFields from "@/views/agro_exporter/requesters/providers/producers/manage_crop_fields.js";
import GenderMixin from "@/components/views/mixins/organization/genders.js";

export default {
  name: "AEProvidersProducersShowProducer",
  mixins: [GenderMixin],
  data() {
    return {
      load_general_data: false,
      proc_button_destroy: false,
      producer: {},
      crop_fields: []
    };
  },
  props: {
    agro_exporter_id: { required: true },
    producer_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.fetchProducer();
        this.fetchCropFields();
      })
    );
  },
  computed: {
    producer_crops: function () {
      return (this.producer.producer_crops || [])
        .map(producer_crop => producer_crop.crop.name)
        .join(", ");
    }
  },
  methods: {
    fetchProducer: async function () {
      let response = await Requester.fetchProducer(
        this.agro_exporter_id,
        this.producer_id
      );

      this.producer = response.data;
      this.load_general_data = true;
    },
    fetchCropFields: async function () {
      let response = await RequesterCropFields.fetchCropFields(
        this.agro_exporter_id,
        this.producer_id
      );

      this.crop_fields = response.data;
    },
    destroyProducer: async function () {
      this.proc_button_destroy = true;

      try {
        let response = await Requester.destroyProducer(
          this.agro_exporter_id,
          this.producer_id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToListProducers();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_button_destroy = false;
    },
    destroyCropField: async function (crop_field) {
      crop_field.proc_button_destroy = true;

      try {
        let response = await RequesterCropFields.destroyCropField(
          this.agro_exporter_id,
          crop_field.id
        );
        if (response.status == 200) {
          this.registerSuccessNotification({
            title: response.data.success_message
          });

          this.crop_fields.splice(this.crop_fields.indexOf(crop_field), 1);
        }
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      crop_field.proc_button_destroy = false;
    },
    goToEditProducer: function () {
      this.$router.push({
        name: this.routeName("edit_producer"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id
        }
      });
    },
    goToNewCropField: function () {
      this.$router.push({
        name: this.routeName("new_crop_field"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id
        }
      });
    },
    goToEditCropField: function (crop_field) {
      this.$router.push({
        name: this.routeName("edit_crop_field"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id,
          crop_field_id: crop_field.id
        }
      });
    },
    goToListProducers: function () {
      this.$router.push({
        name: this.routeName("list_producers"),
        params: {
          agro_exporter_id: this.agro_exporter_id
        }
      });
    }
  }
};
</script>
